import { createEffect, createEvent, createStore, sample } from 'effector';
import { AddonPriceInfo, axiosClient } from '@nx-frontend/shared/api';

export interface AddonTranscribePrice extends AddonPriceInfo {
  isLoaded: boolean;
  isFetching: boolean;
}

export const initialAddonTranscribePriceState: AddonTranscribePrice = {
  isLoaded: false,
  isFetching: false,
  addonPrice: 0,
  totalForAddon: 0,
  totalForNextPeriod: 0,
  membersCount: 0,
  payAmountNow: 0,
  subscriptionTotal: 0,
  quantity: 0,
};

export const $addonTranscribePrice = createStore<AddonTranscribePrice>(initialAddonTranscribePriceState);

export const loadAddonTranscribePriceEvent = createEvent<{ orgId: string; subscriptionId: string; quantity: number }>();
const setAddonTranscribePriceFetchingEvent = createEvent<{ quantity: number }>();

const loadAddonTranscribePriceFx = createEffect(
  async ({ orgId, subscriptionId, quantity }: { orgId: string; subscriptionId: string; quantity: number }) => {
    const { data } = await axiosClient.get<AddonPriceInfo>(
      `/v2/api/billing/orgs/${orgId}/addons/transcribe/priceInfo?subscriptionId=${subscriptionId}&quantity=${quantity}`
    );
    return data;
  }
);

$addonTranscribePrice.on(setAddonTranscribePriceFetchingEvent, (store, payload) => {
  if (store.quantity && store.quantity !== payload.quantity) {
    return Object.assign({}, initialAddonTranscribePriceState, {
      isFetching: true,
    });
  }
  if (store.isFetching || store.isLoaded) {
    return store;
  }
  return Object.assign({}, store, {
    isFetching: true,
  });
});

$addonTranscribePrice.on(loadAddonTranscribePriceFx.doneData, (addonTranscribePrice, data) => {
  return Object.assign({}, addonTranscribePrice, {
    addonPrice: data.addonPrice,
    totalForAddon: data.totalForAddon,
    totalForNextPeriod: data.totalForNextPeriod,
    payAmountNow: data.payAmountNow,
    membersCount: data.membersCount,
    subscriptionTotal: data.subscriptionTotal,
    quantity: data.quantity,
    isLoaded: true,
    isFetching: false,
  });
});

$addonTranscribePrice.on(loadAddonTranscribePriceFx.failData, (addonTranscribePrice, data) => {
  console.error('Fail to fetch addon transcribe price', data);
  return Object.assign({}, addonTranscribePrice, {
    isFetching: false,
  });
});

sample({
  source: $addonTranscribePrice,
  clock: loadAddonTranscribePriceEvent,
  filter(addonTranscribePrice, { orgId, subscriptionId, quantity }) {
    // load for new quantity
    if (addonTranscribePrice.quantity !== 0 && quantity !== 0) {
      return true;
    }
    if (addonTranscribePrice.isFetching || addonTranscribePrice.isLoaded || !orgId || !subscriptionId || !quantity) {
      // do not load if already fetching or loaded
      return false;
    }
    return true;
  },
  fn: (addonTranscribePrice, { orgId, subscriptionId, quantity }) => {
    return {
      orgId,
      subscriptionId,
      quantity,
    };
  },
  target: [loadAddonTranscribePriceFx, setAddonTranscribePriceFetchingEvent],
});
